import { getShortName } from '@/utils/userNames'
import { countLabel } from '@/utils/plural'
import { CurrencyBalance, groupBalancesByMember } from '@/utils/balanceCalculations'
import { PublicSectionGate } from '@components/PublicPage'
import { usePublicExpenses, usePublicOverview } from '@hooks'
import { formatMoney } from '@lib/money'
import { GetPublicExpensesResponse, GetPublicOverviewResponse } from '@orbit/api-types'
import React from 'react'
import { useParams } from 'react-router-dom'
import ActivityFeed from '../Balances/ActivitySection/ActivityFeed'
import { Avatar } from '../Balances/Avatar'
import { groupActivityByDate } from '../Balances/balancesFormat'
import PublicSectionLayout from './PublicSectionLayout'
import { buildTravelerLookup } from './publicMembers'
import {
	BalanceAmount,
	BalanceName,
	BalanceRow,
	ExpensesLayout,
	Panel,
	PanelTitle,
	SettledNote,
} from './PublicExpensesPage.styles'

/** What the page renders from: the money detail plus the travelers that name it. */
interface ExpensesPageData {
	detail: GetPublicExpensesResponse
	travelers: GetPublicOverviewResponse['travelers']
}

/** Primary (largest) balance line for a member → the amount + owe/owed tone to show. */
const summarizeMemberBalance = (
	balances: CurrencyBalance[],
): { tone: 'owe' | 'owed'; label: string } | null => {
	if (balances.length === 0) return null
	const sorted = [...balances].sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
	const primary = sorted[0]
	const more = sorted.length > 1 ? ` +${sorted.length - 1}` : ''
	const amount = formatMoney(Math.abs(primary.amount), primary.currencyCode)
	// Signed net: positive = the group owes this member (they get back); negative = they owe.
	return primary.amount > 0
		? { tone: 'owed', label: `gets back ${amount}${more}` }
		: { tone: 'owe', label: `owes ${amount}${more}` }
}

/**
 * Public read-only money view. Only resolves when the owner has opted expenses
 * into sharing (ADR 0003), so named balances and pay ups are knowingly on the
 * wire. The detail endpoint carries user ids only; names resolve from the
 * overview's `travelers`, so the gate runs on both queries combined — a missing
 * travelers list must never render real debts as "settled up".
 */
const PublicExpensesPage: React.FC = () => {
	const { tripCode } = useParams<{ tripCode: string }>()
	const expensesQuery = usePublicExpenses(tripCode)
	const overviewQuery = usePublicOverview(tripCode)

	const combined = {
		data:
			expensesQuery.data && overviewQuery.data
				? ({ detail: expensesQuery.data, travelers: overviewQuery.data.travelers } satisfies ExpensesPageData)
				: undefined,
		isLoading: expensesQuery.isLoading || overviewQuery.isLoading,
		// The expenses endpoint is the sharing gate, so its error (a deterministic
		// 404 when the section is OFF) takes precedence; an overview-only failure
		// (5xx / network — a 404 split can't happen, expenses' gate is a superset)
		// then surfaces as the transient error state.
		error: expensesQuery.error ?? overviewQuery.error,
	}

	return (
		<PublicSectionGate
			query={combined}
			loadingLabel="Loading expenses…"
			unavailableTitle="Expenses Unavailable"
			unavailableMessage="These expenses don't exist or haven't been shared publicly by the trip owner."
		>
			{({ detail, travelers }) => {
				const { expenses, paymentsOwed, payUps } = detail
				const lookup = buildTravelerLookup(travelers)

				const balanceGroups = groupBalancesByMember(
					paymentsOwed,
					travelers.map(t => t.id),
				).filter(group => group.balancesPerCurrency.length > 0)

				const dateGroups = groupActivityByDate(expenses, payUps)

				// Same rule as the member Balances page: amounts carry their currency
				// code only when more than one currency is in play.
				const currencyCodes = new Set<string>()
				paymentsOwed.forEach(p => currencyCodes.add(p.currencyCode))
				payUps.forEach(p => currencyCodes.add(p.currencyCode))
				expenses.forEach(e => currencyCodes.add(e.currencyCode))
				const showCurrencyCode = currencyCodes.size >= 2

				return (
					<PublicSectionLayout
						tripCode={tripCode ?? ''}
						title="Expenses & Pay Ups"
						subtitle={`${countLabel(expenses.length, 'expense')}`}
					>
						<ExpensesLayout>
							<Panel>
								<PanelTitle>Balances</PanelTitle>
								{balanceGroups.length === 0 ? (
									<SettledNote>Everyone&apos;s settled up ✓</SettledNote>
								) : (
									balanceGroups.map(group => {
										const traveler = lookup(group.userId)
										const summary = summarizeMemberBalance(group.balancesPerCurrency)
										if (!summary) return null
										return (
											<BalanceRow key={group.userId}>
												<Avatar member={traveler} />
												<BalanceName>{getShortName(traveler)}</BalanceName>
												<BalanceAmount $tone={summary.tone}>{summary.label}</BalanceAmount>
											</BalanceRow>
										)
									})
								)}
							</Panel>

							<Panel>
								<PanelTitle>Activity</PanelTitle>
								<ActivityFeed
									groupedActivity={dateGroups}
									people={travelers}
									me={undefined}
									// Receipt attachments are not publicly resolvable (ADR 0003 —
									// the public files surface is trip-vault only), so no thumbs.
									fileUrlById={new Map()}
									showCurrencyCode={showCurrencyCode}
									empty={{
										title: 'No expenses yet',
										description: 'No expenses or pay ups have been added yet.',
									}}
								/>
							</Panel>
						</ExpensesLayout>
					</PublicSectionLayout>
				)
			}}
		</PublicSectionGate>
	)
}

export default PublicExpensesPage
